import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { SxProps, Theme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { ReactNode, useState } from 'react';
import { generatePath, Link as RouterLink } from 'react-router';

import { ProcessOfferItemDto } from '@/api/generated';
import { ComponentSpinner } from '@/components/ui/componentSpinner/ComponentSpinner.comp.tsx';
import { themeColors } from '@/config/theme/themeColors.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { formatDate } from '@/utils/formatDate.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { OfferCalendarVisibilityIndicator } from '@/views/processes/offer/components/calendarVisibility/OfferCalendarVisibilityIndicator.comp.tsx';
import { ProcessOfferEmailsIndicator } from '@/views/processes/offer/components/emails/ProcessOfferEmailsIndicator.comp.tsx';
import { ProcessOfferEmailsTable } from '@/views/processes/offer/components/emails/ProcessOfferEmailsTable.comp.tsx';
import { useGetProcessOfferEmails } from '@/views/processes/offer/components/emails/util/useGetProcessOfferEmails.util.ts';
import { ProcessOfferPdfPreviewDrawer } from '@/views/processes/offer/components/pdfPreview/ProcessOfferPdfPreviewDrawer.comp.tsx';
import { ProcessOfferItemWithChain } from '@/views/processes/utils/sortOffersByChain.util.ts';

const ROW_COLSPAN = 8;

const CHAIN_STYLES = [
  { bg: 'rgba(227, 124, 153, 0.06)', border: themeColors.accentColor },
  { bg: 'rgba(0, 111, 186, 0.06)', border: themeColors.blue },
];

type Props = {
  previewDrawerSx?: SxProps<Theme>;
  renderExtraActions?: (offer: ProcessOfferItemDto) => ReactNode;
  row: ProcessOfferItemWithChain;
};

export const ProcessMultiOfferGroupOfferRow: React.FC<Props> = ({
  previewDrawerSx,
  renderExtraActions,
  row,
}) => {
  const { t } = useTranslationWithPrefix('views.process.multiOffer.details');
  const { t: tEmails } = useTranslationWithPrefix('views.process.offer.emails');
  const [expanded, setExpanded] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { emails, isPending } = useGetProcessOfferEmails({
    enabled: expanded,
    processId: row.processId,
    processOfferId: row.processOfferId,
  });

  const offerPath = generatePath(RouteEnum.PROCESSES_DETAILS_OFFER, {
    multiOfferId: row.processMultiOfferId,
    offerId: row.processOfferId,
    processId: row.processId,
  });

  const creator = `${row.creator.firstName} ${row.creator.lastName}`;
  const chainStyle = CHAIN_STYLES[row.__chainIndex] ?? CHAIN_STYLES[0];

  return (
    <>
      <TableRow
        hover
        sx={{
          '& > td:first-of-type': { borderLeft: `4px solid ${chainStyle.border}` },
          '& > td:last-of-type': { borderBottom: expanded ? 'unset' : undefined },
        }}
      >
        <TableCell sx={{ color: chainStyle.border, fontWeight: 600 }}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <span>v{row.versionNumber}</span>
            {row.sentAt && (
              <Tooltip
                title={t('offerActions.sent', {
                  date: formatDate(row.sentAt, { withTime: true }),
                })}
              >
                <LockOutlinedIcon fontSize="inherit" sx={{ color: themeColors.greyDark }} />
              </Tooltip>
            )}
          </Stack>
        </TableCell>
        <TableCell>{row.name}</TableCell>
        <TableCell>{formatDate(row.eventDateFrom, { withTime: true })}</TableCell>
        <TableCell>{formatDate(row.eventDateTo, { withTime: true })}</TableCell>
        <TableCell>{row.noOfGuests ?? '-'}</TableCell>
        <TableCell>{creator}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <span>{t('processOfferStatusOptions.' + row.status)}</span>
            <OfferCalendarVisibilityIndicator
              status={row.status}
              visibility={row.calendarVisibility}
            />
          </Stack>
        </TableCell>
        <TableCell align="right">
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}
            useFlexGap
          >
            <ProcessOfferEmailsIndicator
              onClick={() => setExpanded(value => !value)}
              processId={row.processId}
              processOfferId={row.processOfferId}
            />
            <Tooltip title={t('offerActions.preview')}>
              <IconButton onClick={() => setIsPreviewOpen(true)} size="small">
                <PictureAsPdfOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('offerActions.open')}>
              <IconButton component={RouterLink} size="small" to={offerPath} viewTransition>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {renderExtraActions?.(row)}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={ROW_COLSPAN} sx={{ borderBottom: 'none', p: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                bgcolor: 'rgba(0,0,0,0.02)',
                border: `1px solid ${themeColors.stroke}`,
                borderRadius: 1,
                m: 1,
                p: 2,
                position: 'relative',
              }}
            >
              <Typography sx={{ mb: 1 }} variant="f14Medium">
                {tEmails('title')}
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <ComponentSpinner isVisible={isPending} />
                {!isPending && <ProcessOfferEmailsTable emails={emails} />}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <ProcessOfferPdfPreviewDrawer
        multiProcessOfferGroupId={row.processMultiOfferId}
        onClose={() => setIsPreviewOpen(false)}
        open={isPreviewOpen}
        processId={row.processId}
        processOfferId={row.processOfferId}
        sx={previewDrawerSx}
      />
    </>
  );
};
